import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';
import { query } from '@warp-drive/utilities/json-api';
import type { PaginationMeta } from '../../../utils/api-types.ts';
import type { ItemStats } from '../../../schemas/item-stats.ts';
import type { Recipe } from '../../../schemas/recipe.ts';
import type ItemsIndexResultsController from '../../../controllers/explore/index/results.ts';

export interface ItemsResultsParams {
  type?: string;
  q?: string;
  sort?: string;
}

export default class ItemsIndexResultsRoute extends Route {
  @service declare store: Store;

  queryParams = {
    type: { refreshModel: true },
    q: { refreshModel: true },
    sort: { refreshModel: true },
  };

  async model(params: Record<string, unknown>) {
    const { type, q, sort } = params as unknown as ItemsResultsParams;
    const activeSort = sort || '-createdAt';

    // eslint-disable-next-line ember/no-controller-access-in-routes
    const controller = this.controllerFor(
      'explore.index.results'
    ) as ItemsIndexResultsController;

    const fetchKey = `${type ?? ''}|${q ?? ''}|${activeSort}`;
    const hasData =
      type === 'recipe'
        ? controller.recipes.length > 0
        : controller.items.length > 0;

    if (controller._lastFetchKey === fetchKey && hasData) {
      return { cached: true as const };
    }

    const queryParams: Record<string, string> = {
      'page[number]': '1',
      'page[size]': '24',
      sort: activeSort,
    };

    if (type === 'recipe') {
      if (q) queryParams.q = q;
      const recipesResult = await this.store.request(
        query('recipe', queryParams)
      );
      const json = recipesResult.content as unknown as {
        data?: Recipe[];
        meta?: PaginationMeta;
      };
      return {
        cached: false as const,
        items: [] as ItemStats[],
        recipes: json.data ?? [],
        meta: json.meta ?? { page: 1, totalPages: 1 },
        fetchKey,
      };
    }

    if (type) queryParams['filter[type]'] = type;
    if (q) queryParams.q = q;

    const itemsResult = await this.store.request(
      query('item-stat', queryParams, {
        resourcePath: 'items/stats',
      })
    );

    const json = itemsResult.content as unknown as {
      data?: ItemStats[];
      meta?: PaginationMeta;
    };

    return {
      cached: false as const,
      items: json.data ?? [],
      recipes: [] as Recipe[],
      meta: json.meta ?? { page: 1, totalPages: 1 },
      fetchKey,
    };
  }

  setupController(
    controller: ItemsIndexResultsController,
    model: Awaited<ReturnType<this['model']>>
  ) {
    super.setupController(controller, model);

    if (model.cached) {
      return;
    }

    controller.items = model.items;
    controller.recipes = model.recipes;
    controller.meta = model.meta;
    controller._lastFetchKey = model.fetchKey;
    controller.isLoadingMore = false;
  }
}
